import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { TrustBadge } from '../components/TrustBadge';
import {
  Trophy,
  TrendingUp,
  Users,
  Medal,
  Crown,
  Zap
} from 'lucide-react';
import { userAPI } from '../lib/api';
import { formatNumber, getAvatarFallback, formatCurrency } from '../lib/utils';
import { useTranslation } from '../context/LanguageContext';

export function LeaderboardPage() {
  const { t } = useTranslation();
  const [supportersData, setSupportersData] = useState([]);
  const [projectsData, setProjectsData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLeaderboards = async () => {
      try {
        const [supportersRes, projectsRes] = await Promise.all([
          userAPI.getLeaderboard('supporters'),
          userAPI.getLeaderboard('projects')
        ]);
        setSupportersData(supportersRes.data.leaderboard);
        setProjectsData(projectsRes.data.leaderboard);
      } catch (error) {
        console.error('Failed to fetch leaderboards:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchLeaderboards();
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center shadow-lg">
              <Trophy className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">{t('leaderboard.title')}</h1>
              <p className="text-slate-600">{t('leaderboard.subtitle')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="supporters">
          <TabsList className="mb-8">
            <TabsTrigger value="supporters" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              {t('leaderboard.topSupporters')}
            </TabsTrigger>
            <TabsTrigger value="projects" className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              {t('leaderboard.topProjects')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="supporters">
            {loading ? (
              <LeaderboardSkeleton />
            ) : supportersData.length > 0 ? (
              <div className="space-y-3">
                {supportersData.map((user, index) => (
                  <SupporterRow key={user.user_id} user={user} rank={index + 1} t={t} />
                ))}
              </div>
            ) : (
              <EmptyState message={t('leaderboard.noSupporters')} />
            )}
          </TabsContent>

          <TabsContent value="projects">
            {loading ? (
              <LeaderboardSkeleton />
            ) : projectsData.length > 0 ? (
              <div className="space-y-3">
                {projectsData.map((project, index) => (
                  <ProjectRow key={project.project_id} project={project} rank={index + 1} t={t} />
                ))}
              </div>
            ) : (
              <EmptyState message={t('leaderboard.noProjectsYet')} />
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function SupporterRow({ user, rank, t }) {
  const getRankIcon = () => {
    if (rank === 1) return <Crown className="w-5 h-5 text-yellow-500" />;
    if (rank === 2) return <Medal className="w-5 h-5 text-slate-400" />;
    if (rank === 3) return <Medal className="w-5 h-5 text-amber-600" />;
    return <span className="w-5 text-center font-bold text-slate-400">#{rank}</span>;
  };

  return (
    <Link
      to={`/profile/${user.user_id}`}
      className="flex items-center gap-4 bg-white rounded-xl border border-slate-200 p-4 hover:border-orange-300 transition-colors"
      data-testid={`supporter-row-${rank}`}
    >
      <div className="w-8 flex justify-center">
        {getRankIcon()}
      </div>

      <Avatar className="w-12 h-12">
        <AvatarImage src={user.picture} alt={user.name} />
        <AvatarFallback className="bg-orange-100 text-orange-700">
          {getAvatarFallback(user.name)}
        </AvatarFallback>
      </Avatar>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-slate-900 truncate">{user.name}</span>
          <TrustBadge level={user.trust_level} size="small" />
        </div>
        <p className="text-sm text-slate-500">{user.email}</p>
      </div>

      <div className="text-right">
        <div className="flex items-center gap-1 text-orange-600 font-bold">
          <Zap className="w-4 h-4" />
          {formatNumber(user.total_boosts_given)}
        </div>
        <p className="text-xs text-slate-500">{t('leaderboard.boostsGiven')}</p>
      </div>
    </Link>
  );
}

function ProjectRow({ project, rank, t }) {
  const getRankIcon = () => {
    if (rank === 1) return <Crown className="w-5 h-5 text-yellow-500" />;
    if (rank === 2) return <Medal className="w-5 h-5 text-slate-400" />;
    if (rank === 3) return <Medal className="w-5 h-5 text-amber-600" />;
    return <span className="w-5 text-center font-bold text-slate-400">#{rank}</span>;
  };

  return (
    <Link
      to={`/project/${project.project_id}`}
      className="flex items-center gap-4 bg-white rounded-xl border border-slate-200 p-4 hover:border-orange-300 transition-colors"
      data-testid={`project-row-${rank}`}
    >
      <div className="w-8 flex justify-center">
        {getRankIcon()}
      </div>

      <div className="w-16 h-16 rounded-xl overflow-hidden bg-slate-100 flex-shrink-0">
        <img
          src={project.image_url || 'https://images.pexels.com/photos/3912477/pexels-photo-3912477.jpeg'}
          alt={project.title}
          className="w-full h-full object-cover"
        />
      </div>

      <div className="flex-1 min-w-0">
        <h3 className="font-semibold text-slate-900 truncate">{project.title}</h3>
        <p className="text-sm text-slate-500 truncate">{project.tagline}</p>
        <div className="flex items-center gap-3 mt-1">
          <Badge variant="secondary" className="text-xs capitalize">
            {project.category}
          </Badge>
          <span className="text-xs text-slate-400">
            {t('leaderboard.supporters', { count: formatNumber(project.unique_supporters) })}
          </span>
        </div>
      </div>

      <div className="text-right">
        <div className="flex items-center gap-1 text-orange-600 font-bold text-lg">
          <TrendingUp className="w-4 h-4" />
          {formatNumber(Math.round(project.momentum_score))}
        </div>
        <p className="text-xs text-slate-500">{t('leaderboard.momentum')}</p>
        {project.total_money_raised > 0 && (
          <p className="text-xs text-green-600 mt-1">
            {t('leaderboard.raised', { amount: formatCurrency(project.total_money_raised) })}
          </p>
        )}
      </div>
    </Link>
  );
}

function LeaderboardSkeleton() {
  return (
    <div className="space-y-3">
      {[...Array(5)].map((_, i) => (
        <div key={i} className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-4">
            <div className="w-8 h-8 bg-slate-200 rounded skeleton-shimmer" />
            <div className="w-12 h-12 bg-slate-200 rounded-full skeleton-shimmer" />
            <div className="flex-1">
              <div className="w-32 h-5 bg-slate-200 rounded skeleton-shimmer mb-2" />
              <div className="w-48 h-4 bg-slate-200 rounded skeleton-shimmer" />
            </div>
            <div className="w-16 h-8 bg-slate-200 rounded skeleton-shimmer" />
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ message }) {
  return (
    <div className="text-center py-16 bg-white rounded-2xl border border-slate-200">
      <Trophy className="w-12 h-12 mx-auto text-slate-300 mb-4" />
      <p className="text-slate-500">{message}</p>
    </div>
  );
}
